import React, { useEffect, useState } from "react";
import styles from "./Subscription.module.scss";
import {
  getSubscriptionStatus,
  getTrialStatus,
  startTrial,
  createCheckoutSession,
  createChangeSession,
  cancelSubscription,
  reactivateSubscription,
  createBillingPortalSession,
} from "../services/api";

// Map your Stripe Price IDs (monthly/annual)
const priceIdMap: Record<string, string> = {
  basic_monthly: "price_1RmMkZDmXTWYhdQ9Sz0rTgI6",
  pro_monthly:   "price_1RmMnCDmXTWYhdQ91Yttwetd",
  basic_annual:  "price_1RmMnlDmXTWYhdQ9RcgVcWt1",
  pro_annual:    "price_1RmMoBDmXTWYhdQ9tL79OlFs",
};

type BillingCycle = "monthly" | "annual";

function normalizePlanLabel(raw?: string | null) {
  const v = (raw || "").toLowerCase();
  if (v.includes("beta")) return "PRO (Beta Gratuit)";
  if (!raw || !raw.trim()) return "—";
  return String(raw);
}

const is404 = (e: any) =>
  e?.response?.status === 404 ||
  e?.status === 404 ||
  (typeof e?.message === "string" && e.message.includes("404"));

const betaMsg = "Facturation indisponible pendant la Beta gratuite.";

const Subscription: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [message, setMessage] = useState<string>("");
  const [subscription, setSubscription] = useState<any>(null);
  const [trialStatus, setTrialStatusState] = useState<"none" | "active" | "expired">("none");
  const [currentPlanLabel, setCurrentPlanLabel] = useState<string>("—");
  const [isBeta, setIsBeta] = useState<boolean>(false);

  // Initial status load
  useEffect(() => {
    (async () => {
      try {
        const [sub, trial] = await Promise.all([
          getSubscriptionStatus(),
          getTrialStatus(),
        ]);

        const subObj = sub?.subscription || null;
        setSubscription(subObj);
        setTrialStatusState(trial?.trialStatus || "none");

        const rawPlan = subObj?.plan;
        const planLooksBeta = String(rawPlan || "").toLowerCase() === "beta";

        setIsBeta(planLooksBeta);

        if (sub?.hasActiveSubscription && rawPlan) {
          setCurrentPlanLabel(normalizePlanLabel(rawPlan));
        } else if (trial?.trialStatus === "active") {
          setCurrentPlanLabel("Essai gratuit");
        } else {
          setCurrentPlanLabel("—");
        }
      } catch (e: any) {
        // Treat 404 as "beta mode" (no billing yet)
        if (is404(e)) {
          setIsBeta(true);
          setCurrentPlanLabel("PRO (Beta Gratuit)");
          setMessage(betaMsg);
        } else {
          setMessage(e?.message || "Erreur de chargement.");
        }
      } finally {
        setLoadingStatus(false);
      }
    })();
  }, []);

  const choosePriceId = (planKey: "basic" | "pro", cycle: BillingCycle) => {
    const priceId = priceIdMap[`${planKey}_${cycle}`];
    if (!priceId) throw new Error("Plan indisponible.");
    return priceId;
  };

  const betaBlocked = () => setMessage("Indisponible pendant la Beta gratuite.");

  // --- Actions (with Beta block) ---
  const handleStartTrial = async () => {
    if (isBeta) return betaBlocked();
    setLoading(true);
    setMessage("");
    try {
      const d = await startTrial();
      if (!d?.success) throw new Error(d?.message || "Erreur lors du démarrage de l'essai.");
      setTrialStatusState("active");
      setCurrentPlanLabel("Essai gratuit");
      setMessage("Essai gratuit démarré ✅");
    } catch (e: any) {
      setMessage(is404(e) ? betaMsg : e.message || "Erreur essai gratuit.");
      if (is404(e)) setIsBeta(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (planKey: "basic" | "pro") => {
    if (isBeta) return betaBlocked();
    setLoading(true);
    setMessage("");
    try {
      const url = (await createCheckoutSession(choosePriceId(planKey, billingCycle))).url;
      if (!url) throw new Error("Impossible de créer la session de paiement.");
      window.location.href = url;
    } catch (e: any) {
      setMessage(is404(e) ? betaMsg : e.message || "Erreur paiement.");
      if (is404(e)) setIsBeta(true);
      setLoading(false);
    }
  };

  const handleChangePlan = async (planKey: "basic" | "pro") => {
    if (isBeta) return betaBlocked();
    setLoading(true);
    setMessage("");
    try {
      const url = (await createChangeSession(choosePriceId(planKey, billingCycle))).url;
      if (!url) throw new Error("Impossible de changer de plan.");
      window.location.href = url;
    } catch (e: any) {
      setMessage(is404(e) ? betaMsg : e.message || "Erreur changement de plan.");
      if (is404(e)) setIsBeta(true);
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (isBeta) return betaBlocked();
    if (!window.confirm("Confirmez-vous l’annulation de votre abonnement ?")) return;
    setLoading(true);
    setMessage("");
    try {
      const d = await cancelSubscription();
      if (!d?.success) throw new Error(d?.message || "Annulation impossible.");
      window.location.reload();
    } catch (e: any) {
      setMessage(is404(e) ? betaMsg : e.message || "Erreur d'annulation.");
      if (is404(e)) setIsBeta(true);
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (isBeta) return betaBlocked();
    setLoading(true);
    setMessage("");
    try {
      const d = await reactivateSubscription();
      if (!d?.success) throw new Error(d?.message || "Réactivation impossible.");
      window.location.reload();
    } catch (e: any) {
      setMessage(is404(e) ? betaMsg : e.message || "Erreur de réactivation.");
      if (is404(e)) setIsBeta(true);
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    if (isBeta) return betaBlocked();
    setLoading(true);
    setMessage("");
    try {
      const d = await createBillingPortalSession();
      if (!d?.url) throw new Error("Portail de facturation indisponible.");
      window.location.href = d.url;
    } catch (e: any) {
      setMessage(is404(e) ? betaMsg : e.message || "Erreur portail de facturation.");
      if (is404(e)) setIsBeta(true);
      setLoading(false);
    }
  };

  const isActive = subscription?.status === "active";
  const isCanceled = subscription?.cancelAtPeriodEnd === true;

  return (
    <main className={styles.subscriptionContainer}>
      <h1 className={styles.subscriptionTitle}>Abonnement & Paiement</h1>

      {/* Beta Badge */}
      {isBeta && (
        <div
          style={{
            background: "#2563eb",
            color: "white",
            padding: "8px 14px",
            borderRadius: "8px",
            marginBottom: "12px",
            fontWeight: 600,
            display: "inline-block",
          }}
        >
          🎉 Vous utilisez la version Beta gratuite (PRO)
        </div>
      )}

      {/* Beta explanatory note */}
      {isBeta && (
        <div
          style={{
            background: "#eff6ff",
            color: "#1e40af",
            border: "1px solid #bfdbfe",
            padding: "10px 14px",
            borderRadius: 8,
            marginBottom: 16,
          }}
          role="note"
        >
          La facturation et le portail de paiement sont désactivés pendant la période Beta.
        </div>
      )}

      {loadingStatus ? (
        <div className={styles.loadingBox}>Chargement…</div>
      ) : (
        <>
          {/* Current plan box */}
          <div className={styles.subscriptionCurrentPlan}>
            <strong>Plan actuel :</strong> {currentPlanLabel}
            {subscription && (
              <div className={styles.subscriptionDetails}>
                <p>
                  Statut:{" "}
                  <span className={isActive ? styles.active : styles.inactive}>
                    {subscription.status || "—"}
                  </span>
                </p>
                {typeof subscription.cancelAtPeriodEnd === "boolean" && (
                  <p className={subscription.cancelAtPeriodEnd ? styles.cancelWarning : undefined}>
                    Renouvellement :{" "}
                    {subscription.cancelAtPeriodEnd ? "arrêt à la fin de la période" : "actif"}
                  </p>
                )}
                {subscription.currentPeriodEnd && (
                  <p>
                    Échéance :{" "}
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString("fr-FR")}
                  </p>
                )}
              </div>
            )}

            {/* Actions for cancel/reactivate + portal */}
            <div className={styles.subscriptionActions}>
              <button
                className={styles.cancelBtn}
                disabled={loading || isBeta}
                onClick={openBillingPortal}
              >
                Gérer la facturation
              </button>

              {isActive && !isCanceled && (
                <button
                  className={styles.cancelBtn}
                  disabled={loading || isBeta}
                  onClick={handleCancel}
                >
                  Annuler le renouvellement
                </button>
              )}

              {isCanceled && (
                <button
                  className={styles.reactivateBtn}
                  disabled={loading || isBeta}
                  onClick={handleReactivate}
                >
                  Réactiver
                </button>
              )}
            </div>

            {message && <div className={styles.subscriptionMessage}>{message}</div>}
          </div>

          {/* Billing cycle toggle */}
          <div className={styles.billingToggle}>
            <button
              type="button"
              className={`${styles.toggleBtn} ${billingCycle === "monthly" ? styles.activeToggle : ""}`}
              aria-pressed={billingCycle === "monthly"}
              onClick={() => setBillingCycle("monthly")}
            >
              Mensuel
            </button>
            <button
              type="button"
              className={`${styles.toggleBtn} ${billingCycle === "annual" ? styles.activeToggle : ""}`}
              aria-pressed={billingCycle === "annual"}
              onClick={() => setBillingCycle("annual")}
            >
              Annuel <span className={styles.savings}>2 mois offerts</span>
            </button>
          </div>

          {/* Plans grid */}
          <section className={styles.subscriptionPlans}>
            {/* Trial */}
            <article className={styles.subscriptionPlanCard}>
              <div className={styles.subscriptionPlanName}>Essai gratuit</div>
              <div className={styles.subscriptionPlanDesc}>14 jours – toutes fonctionnalités</div>
              <button
                className={styles.subscriptionPlanBtn}
                disabled={loading || trialStatus === "active" || isActive || isBeta}
                onClick={handleStartTrial}
              >
                Démarrer l’essai
              </button>
            </article>

            {/* Basic */}
            <article className={styles.subscriptionPlanCard}>
              <div className={styles.subscriptionPlanName}>Basic</div>
              <div className={styles.subscriptionPlanPrice}>
                {billingCycle === "monthly" ? "19€ / mois" : "190€ / an"}
              </div>
              <div className={styles.subscriptionPlanDesc}>Pour démarrer sereinement</div>
              {isActive && (subscription?.plan || "").toLowerCase().includes("pro") ? (
                <button
                  className={styles.subscriptionPlanBtn}
                  disabled={loading || isBeta}
                  onClick={() => handleChangePlan("basic")}
                >
                  Passer à Basic
                </button>
              ) : (
                <button
                  className={styles.subscriptionPlanBtn}
                  disabled={loading || isBeta}
                  onClick={() => handleCheckout("basic")}
                >
                  Choisir Basic
                </button>
              )}
            </article>

            {/* Pro */}
            <article className={styles.subscriptionPlanCard}>
              <div className={styles.subscriptionPlanName}>Pro</div>
              <div className={styles.subscriptionPlanPrice}>
                {billingCycle === "monthly" ? "39€ / mois" : "390€ / an"}
              </div>
              <div className={styles.subscriptionPlanDesc}>Pour aller plus loin</div>
              {isActive && (subscription?.plan || "").toLowerCase().includes("basic") ? (
                <button
                  className={styles.subscriptionPlanBtn}
                  disabled={loading || isBeta}
                  onClick={() => handleChangePlan("pro")}
                >
                  Passer à Pro
                </button>
              ) : (
                <button
                  className={styles.subscriptionPlanBtn}
                  disabled={loading || isBeta}
                  onClick={() => handleCheckout("pro")}
                >
                  Choisir Pro
                </button>
              )}
            </article>
          </section>

          <p className={styles.subscriptionInfo}>
            {isBeta
              ? "Vous êtes sur la Beta gratuite : la facturation est désactivée."
              : "Vous pouvez gérer votre moyen de paiement et télécharger vos factures à tout moment."}
            <br />
            <button
              type="button"
              className={styles.billingLink}
              onClick={openBillingPortal}
              disabled={loading || isBeta}
            >
              Ouvrir le portail de facturation
            </button>
          </p>
        </>
      )}
    </main>
  );
};

export default Subscription;
